import { apiClient } from '@/lib/apiClient';

import type {
  ConfirmarFotoRequest,
  PresignedUploadRequest,
  PresignedUploadResponse,
} from '../common/types';
import { assertAllowedPhotoFile, uploadToS3 } from '../common/fotos';

import type { Ectoparasitos, EctoparasitosCreate, EctoparasitosUpdate } from './types';

const base = (uidProtocolo: string) => `/protocolos/${uidProtocolo}/analisis-ectoparasitos`;

export const ectoparasitosApi = {
  // ─── CRUD ───────────────────────────────────────────────

  async create(
    uidProtocolo: string,
    data: EctoparasitosCreate,
    signal?: AbortSignal
  ): Promise<Ectoparasitos> {
    const response = await apiClient.post<Ectoparasitos>(base(uidProtocolo), data, { signal });
    return response.data;
  },

  async listByProtocolo(uidProtocolo: string, signal?: AbortSignal): Promise<Ectoparasitos[]> {
    const response = await apiClient.get<Ectoparasitos[]>(base(uidProtocolo), { signal });
    return response.data;
  },

  async getById(
    uidProtocolo: string,
    uidAnalisis: string,
    signal?: AbortSignal
  ): Promise<Ectoparasitos> {
    const response = await apiClient.get<Ectoparasitos>(
      `${base(uidProtocolo)}/${uidAnalisis}`,
      { signal }
    );
    return response.data;
  },

  async update(
    uidProtocolo: string,
    uidAnalisis: string,
    data: EctoparasitosUpdate,
    signal?: AbortSignal
  ): Promise<Ectoparasitos> {
    const response = await apiClient.patch<Ectoparasitos>(
      `${base(uidProtocolo)}/${uidAnalisis}`,
      data,
      { signal }
    );
    return response.data;
  },

  async remove(uidProtocolo: string, uidAnalisis: string, signal?: AbortSignal): Promise<void> {
    await apiClient.delete(`${base(uidProtocolo)}/${uidAnalisis}`, { signal });
  },

  // ─── Fotos (mismo patrón que SedimentacionFh) ────────────

  async requestPresignedUpload(
    uidProtocolo: string,
    uidAnalisis: string,
    body: PresignedUploadRequest,
    signal?: AbortSignal
  ): Promise<PresignedUploadResponse> {
    const response = await apiClient.post<PresignedUploadResponse>(
      `${base(uidProtocolo)}/${uidAnalisis}/fotos`,
      body,
      { signal }
    );
    return response.data;
  },

  async confirmarFoto(
    uidProtocolo: string,
    uidAnalisis: string,
    body: ConfirmarFotoRequest,
    signal?: AbortSignal
  ): Promise<Ectoparasitos> {
    const response = await apiClient.post<Ectoparasitos>(
      `${base(uidProtocolo)}/${uidAnalisis}/fotos/confirmar`,
      body,
      { signal }
    );
    return response.data;
  },

  async eliminarFoto(
    uidProtocolo: string,
    uidAnalisis: string,
    uidFoto: string,
    signal?: AbortSignal
  ): Promise<Ectoparasitos> {
    const response = await apiClient.delete<Ectoparasitos>(
      `${base(uidProtocolo)}/${uidAnalisis}/fotos/${uidFoto}`,
      { signal }
    );
    return response.data;
  },

  async editarCaption(
    uidProtocolo: string,
    uidAnalisis: string,
    uidFoto: string,
    caption: string | null,
    signal?: AbortSignal
  ): Promise<Ectoparasitos> {
    const response = await apiClient.patch<Ectoparasitos>(
      `${base(uidProtocolo)}/${uidAnalisis}/fotos/${uidFoto}/caption`,
      { caption },
      { signal }
    );
    return response.data;
  },

  async uploadFoto(
    uidProtocolo: string,
    uidAnalisis: string,
    file: File,
    caption: string | null = null,
    signal?: AbortSignal
  ): Promise<Ectoparasitos> {
    assertAllowedPhotoFile(file);

    const presigned = await this.requestPresignedUpload(
      uidProtocolo,
      uidAnalisis,
      { content_type: file.type, caption },
      signal
    );

    await uploadToS3(presigned.upload_url, file);

    return await this.confirmarFoto(
      uidProtocolo,
      uidAnalisis,
      { uid_foto: presigned.uid_foto, content_type: file.type, caption: presigned.caption },
      signal
    );
  },
};
